import { useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";

/**
 * Resort palms around the open pool. The purchased pack has no tree meshes
 * (Unity trailer trees are Standard Assets, not in the FBX set), so these are
 * procedural ribbons. Placement is a FULL ring just off the deck — north,
 * east, west, AND the south walk the spawn camera looks from. Water basins
 * stay a hard keep-out; belt trunks stay on the grass, not the walkway.
 */

function hash(i: number, seed: number): number {
  const n = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

/** One palm leaf: a single bent, tapered ribbon. */
function makeFrondGeometry(): THREE.BufferGeometry {
  const segs = 12;
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= segs; i += 1) {
    const t = i / segs;
    const z = t * 3.1;
    const y = 0.14 * Math.sin(t * Math.PI) - t * t * 1.7;
    const w = Math.sin(t * Math.PI) * 0.42 + 0.02;
    pos.push(-w, y, z, w, y, z);
  }
  for (let i = 0; i < segs; i += 1) {
    const v = i * 2;
    idx.push(v, v + 1, v + 3, v, v + 3, v + 2);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  return geo;
}

const EMPTY_POTS: [number, number][] = [];

/** Water basins in the pool scenario (glTF metres). */
const LAP = { hx: 5.6, hz: 8.4 };
const JACUZZI = { x: 10, z: 10, r: 4.6 };

/** Deck half-extents from pool-walkable.scenario.ts. */
const DECK_X = 15.0;
const DECK_Z = 13.0;

function inWater(x: number, z: number): boolean {
  if (Math.abs(x) < LAP.hx && Math.abs(z) < LAP.hz) return true;
  const dx = x - JACUZZI.x;
  const dz = z - JACUZZI.z;
  return dx * dx + dz * dz < JACUZZI.r * JACUZZI.r;
}

function onDeck(x: number, z: number): boolean {
  return Math.abs(x) < DECK_X - 0.4 && Math.abs(z) < DECK_Z - 0.4;
}

function allowed(x: number, z: number, fromPot = false): boolean {
  if (inWater(x, z)) return false;
  // Belt trees live on the lawn. Potted palms may sit on the deck edge.
  if (!fromPot && onDeck(x, z)) return false;
  return true;
}

type Plant = { x: number; z: number; s: number; yaw: number };

export function PalmBelt({
  count = 52,
  radius = 18,
  y = 0,
  pots = EMPTY_POTS,
}: {
  count?: number;
  radius?: number;
  y?: number;
  pots?: [number, number][];
}) {
  const plants = useMemo(() => {
    const list: Plant[] = [];
    const add = (x: number, z: number, s: number, yaw: number, fromPot = false) => {
      if (!allowed(x, z, fromPot)) return;
      list.push({ x, z, s, yaw });
    };

    // Inner ring: just off the deck so trees read in the spawn view
    // (looking north from z≈10.6). Outer ring adds depth.
    const innerOff = 1.7;
    const outerOff = 4.6;
    const northZ = -(DECK_Z + innerOff);
    const southZ = DECK_Z + innerOff;
    const westX = -(DECK_X + innerOff);
    const eastX = DECK_X + innerOff;

    const northN = Math.max(7, Math.round(count * 0.22));
    for (let i = 0; i < northN; i += 1) {
      const t = i / Math.max(northN - 1, 1);
      const x = -17 + t * 34 + (hash(i, 1) - 0.5) * 1.6;
      add(x, northZ + (hash(i, 2) - 0.5) * 1.1, 1.15 + hash(i, 3) * 0.4, (hash(i, 4) - 0.5) * 0.5);
      add(
        x + (hash(i, 11) - 0.5) * 2,
        northZ - outerOff + (hash(i, 12) - 0.5) * 1.4,
        1.35 + hash(i, 13) * 0.45,
        (hash(i, 14) - 0.5) * 0.6,
      );
    }

    // SOUTH — the spawn screenshot. Trees on the lawn behind / beside the
    // player, plus the near left/right corners that frame the pool.
    const southN = Math.max(7, Math.round(count * 0.22));
    for (let i = 0; i < southN; i += 1) {
      const t = i / Math.max(southN - 1, 1);
      const x = -17 + t * 34 + (hash(i, 21) - 0.5) * 1.6;
      add(x, southZ + (hash(i, 22) - 0.5) * 1.1, 1.1 + hash(i, 23) * 0.4, (hash(i, 24) - 0.5) * 0.5);
      add(
        x + (hash(i, 25) - 0.5) * 2,
        southZ + outerOff + (hash(i, 26) - 0.5) * 1.2,
        1.3 + hash(i, 27) * 0.4,
        (hash(i, 28) - 0.5) * 0.6,
      );
    }

    const sideN = Math.max(6, Math.round(count * 0.18));
    for (const side of [-1, 1] as const) {
      const xInner = side === -1 ? westX : eastX;
      for (let i = 0; i < sideN; i += 1) {
        const t = i / Math.max(sideN - 1, 1);
        const z = -12.5 + t * 25 + (hash(i + side, 31) - 0.5) * 1.4;
        add(
          xInner + side * (hash(i, 32) - 0.3) * 1.2,
          z,
          1.05 + hash(i + 9, 33) * 0.4,
          side * 1.15,
        );
        add(
          xInner + side * (outerOff + hash(i, 34) * 1.4),
          z + (hash(i, 35) - 0.5) * 1.6,
          1.25 + hash(i, 36) * 0.4,
          side * 1.3,
        );
      }
    }

    // Corner clusters that sit in the spawn FOV (near left / near right).
    const corners: [number, number][] = [
      [-16.6, 11.2],
      [-17.4, 8.4],
      [16.6, 11.2],
      [17.4, 8.2],
      [-16.6, -11.2],
      [16.6, -11.2],
    ];
    corners.forEach(([cx, cz], i) => {
      add(cx, cz, 1.2 + hash(i, 41) * 0.25, hash(i, 42) * Math.PI * 2);
    });

    // Small palms sit only in pack planters (terrace). Full trees stay on grass.
    pots.forEach(([px, pz], i) => {
      add(px, pz, 0.42 + hash(i, 8) * 0.06, hash(i, 9) * Math.PI * 2, true);
    });
    return list;
  }, [count, radius, pots]);

  const trunkRef = useRef<THREE.InstancedMesh>(null);
  const frondRef = useRef<THREE.InstancedMesh>(null);
  const frondGeo = useMemo(() => makeFrondGeometry(), []);
  const frondsPer = 13;
  const totalTrees = Math.max(plants.length, 1);
  const totalFronds = totalTrees * frondsPer;

  useLayoutEffect(() => {
    const trunks = trunkRef.current;
    const fronds = frondRef.current;
    if (!trunks || !fronds) return;
    const dummy = new THREE.Object3D();
    let fi = 0;
    plants.forEach((p, ti) => {
      const trunkH = 5.2 * p.s;
      dummy.position.set(p.x, y + trunkH / 2, p.z);
      dummy.rotation.set(0, p.yaw, 0);
      dummy.scale.set(p.s, p.s, p.s);
      dummy.updateMatrix();
      trunks.setMatrixAt(ti, dummy.matrix);

      for (let k = 0; k < frondsPer; k += 1) {
        const yawK = p.yaw + (k / frondsPer) * Math.PI * 2;
        const droop = 0.68 + (k % 4) * 0.07;
        dummy.position.set(p.x, y + trunkH * 0.97, p.z);
        dummy.rotation.set(droop, yawK, 0);
        dummy.scale.setScalar(p.s * (0.95 + (k % 3) * 0.08));
        dummy.updateMatrix();
        fronds.setMatrixAt(fi, dummy.matrix);
        fi += 1;
      }
    });
    trunks.count = plants.length;
    fronds.count = plants.length * frondsPer;
    trunks.instanceMatrix.needsUpdate = true;
    fronds.instanceMatrix.needsUpdate = true;
    trunks.computeBoundingSphere();
    fronds.computeBoundingSphere();
  }, [plants, y, frondsPer]);

  useLayoutEffect(() => {
    return () => frondGeo.dispose();
  }, [frondGeo]);

  return (
    <group name="palm_belt">
      <instancedMesh
        ref={trunkRef}
        args={[undefined, undefined, totalTrees]}
        castShadow
        receiveShadow
      >
        <cylinderGeometry args={[0.09, 0.2, 5.2, 8]} />
        <meshStandardMaterial color="#6b4a2e" roughness={0.92} />
      </instancedMesh>
      <instancedMesh ref={frondRef} args={[frondGeo, undefined, totalFronds]} castShadow>
        <meshStandardMaterial color="#3a8a38" roughness={0.68} side={THREE.DoubleSide} />
      </instancedMesh>
    </group>
  );
}
